import React, { useCallback, useEffect, useRef, useState } from "react";
import classes from "./memory.module.css";
import CustomHeader from "../../components/Header/CustomHeader";
import { STRINGS } from "./strings";
import MemoryOption from "./components/MemoryOption";
import SummaryItem from "./components/SummaryItem";
import MemoryEmptyState from "./components/MemoryEmptyState";
import MemorySourceModal from "./components/MemorySourceModal";
import MemoryProcessingModal from "./components/MemoryProcessingModal";
import MemoryPreviewModal from "./components/MemoryPreviewModal";
import MemoryDeleteModal from "./components/MemoryDeleteModal";
import {
  LANDING_PAGE,
  PDF_IMAGE,
  NOTE_ICON,
} from "../../utils/constants/assets";
import { BASE } from "../../utils/constants/colors";
import { fontWeight } from "../../utils/constants/UI";
import TypographyInline from "../../components/Typography/TypographyInline";
import {
  postRequest,
  getRequest,
  patchRequest,
  deleteRequest,
} from "../../Apis/apiWrapper";
import { authEndpoints } from "../../utils/constants/httpConstants";
import { useDispatch } from "react-redux";
import { updateAvailableCredits } from "../../redux/actions/authActions";
import { useNavigate } from "react-router-dom";
import DashboardLoader from "../../components/Loaders/DashboardLoader";
import EventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../utils/constants";
import useSummaryProgress from "../../hooks/useSummaryProgress";

const JOB_STORAGE_KEYS = {
  id: "memory_summary_job_id",
  source: "memory_summary_job_source",
  startTime: "memory_summary_start_time",
};

const JOB_TIMEOUT_MS = 90000;
const POLL_DELAY_MS = 5000;

const EMPTY_SUMMARY_META = {
  message: "",
  recommendation: "",
  confidence: "",
  grounded: null,
  reason: "",
  source: "",
  scrapeMetadata: null,
  hasUsableSummary: false,
  summaryMode: null,
  type: null,
  summary: "",
};

const INITIAL_MANUAL_FIELDS = {
  website: "",
  companyName: "",
  businessDescription: "",
  awards: "",
};

const SOURCE_CONFIG = {
  website: {
    id: "website",
    type: 1,
    label: "Website",
    modalTitle: "Learn from a website",
    description: "Fastest option for public pages and general company context.",
    qualityHint: "Fastest but may miss protected pages",
    asset: LANDING_PAGE,
    cta: "Generate from Website",
  },
  pdf: {
    id: "pdf",
    type: 2,
    label: "PDF",
    modalTitle: "Upload a PDF",
    description:
      "Best for structured documents like decks, one-pagers, and brochures.",
    qualityHint: "Most accurate source",
    asset: PDF_IMAGE,
    cta: "Generate from PDF",
  },
  manual: {
    id: "manual",
    type: 3,
    label: "Manual",
    modalTitle: "Add internal knowledge",
    description: "Useful for company details that are not available publicly.",
    qualityHint: "Best for internal knowledge",
    asset: NOTE_ICON,
    cta: "Generate from Manual Input",
  },
};

const PROCESSING_STAGES = [
  "Preparing job",
  "Collecting source data",
  "Analyzing company information",
  "Generating structured summary",
  "Finalizing knowledge base",
];

const PROCESSING_TIPS = [
  "Tip: Uploading a PDF usually produces the most accurate summaries.",
  "Tip: Large websites can take longer to analyze.",
  "Tip: Manual descriptions help personalize your company profile.",
];

const breadcrumbs = [{ id: "memory", label: STRINGS.MEMORY_HEADER }];

const hasVisibleSummary = (summary = "") =>
  summary
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;

const formatElapsedTime = (elapsedMs = 0) => {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
};

const getSourceKeyByType = (type) =>
  Object.values(SOURCE_CONFIG).find((item) => item.type === type)?.id || null;

const getSourceTypeByKey = (key) => SOURCE_CONFIG[key]?.type || null;

const getSourceLabel = (sourceKey) =>
  SOURCE_CONFIG[sourceKey]?.label || "AI Summary";

const getStageIndex = (status, elapsedMs) => {
  if (!status) return 0;
  if (status === "completed") return PROCESSING_STAGES.length - 1;
  if (status === "failed" || status === "timeout") {
    return Math.min(
      PROCESSING_STAGES.length - 1,
      Math.max(1, Math.floor(elapsedMs / 15000)),
    );
  }
  if (status === "queued" || status === "preparing") return 0;

  const runningStage = 1 + Math.floor(elapsedMs / 15000);
  return Math.min(PROCESSING_STAGES.length - 1, runningStage);
};

const getStageProgress = (status, elapsedMs) => {
  if (!status) return 0;
  if (status === "completed") return 100;
  if (status === "failed" || status === "timeout") {
    return Math.min(95, 18 + getStageIndex(status, elapsedMs) * 18);
  }
  if (status === "preparing") return 10;
  if (status === "queued") return 16;

  const stageIndex = getStageIndex(status, elapsedMs);
  const boundedElapsed = Math.min(elapsedMs, JOB_TIMEOUT_MS);
  const timeRatio = boundedElapsed / JOB_TIMEOUT_MS;
  return Math.min(94, 24 + stageIndex * 16 + Math.round(timeRatio * 18));
};

const extractEditorHtml = (value) => {
  if (typeof value === "string") return value;
  return value?.target?.innerHTML || "";
};

const escapeAttr = (value) => value.replace(/"/g, "&quot;");

const convertMarkdownLinks = (text) =>
  text.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_, label, url) =>
      `<a href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );

const wrapBulletGroupsAndParagraphs = (block) => {
  const lines = block.split(/\n+/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) return "";

  const out = [];
  let listBuffer = [];

  const flushList = () => {
    if (listBuffer.length) {
      out.push(`<ul>${listBuffer.map((li) => `<li>${li}</li>`).join("")}</ul>`);
      listBuffer = [];
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    const bulletMatch = line.match(/^[-*]\s+(.*)$/);
    if (bulletMatch) {
      listBuffer.push(bulletMatch[1]);
    } else {
      flushList();
      out.push(`<p>${line}</p>`);
    }
  });

  flushList();
  return out.join("");
};

const normalizeSummaryHtml = (raw) => {
  if (!raw || typeof raw !== "string") return raw || "";

  let html = raw.trim();
  if (html.startsWith("<div>") && html.endsWith("</div>")) {
    html = html.slice(5, -6);
  }

  html = convertMarkdownLinks(html);

  const blockTagPattern =
    /<(h[1-6]|p|ul|ol|li|blockquote|pre|table|hr)\b[^>]*>[\s\S]*?<\/\1>|<(hr|br)\s*\/?>/gi;

  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = blockTagPattern.exec(html)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: html.slice(lastIndex, match.index) });
    }
    parts.push({ type: "block", value: match[0] });
    lastIndex = blockTagPattern.lastIndex;
  }
  if (lastIndex < html.length) {
    parts.push({ type: "text", value: html.slice(lastIndex) });
  }

  return parts
    .map((part) =>
      part.type === "block"
        ? part.value
        : wrapBulletGroupsAndParagraphs(part.value),
    )
    .join("");
};

const normalizeSummaryPayload = (response, fallbackType) => {
  const data = response?.data?.data || {};
  const summary = normalizeSummaryHtml(data.summary || "");
  const job = data.job || {};

  return {
    summary,
    type: data.type || fallbackType,
    message: response?.data?.message || "",
    recommendation: data.recommendation || job.recommendation || "",
    confidence:
      data.confidence ||
      job.confidence ||
      data.scrape_metadata?.confidence ||
      "",
    grounded:
      typeof data.grounded === "boolean"
        ? data.grounded
        : typeof job.grounded === "boolean"
          ? job.grounded
          : (data.scrape_metadata?.grounded ?? null),
    reason: data.reason || job.reason || data.scrape_metadata?.reason || "",
    source: data.source || data.scrape_metadata?.url || "",
    scrapeMetadata: data.scrape_metadata || null,
    hasUsableSummary: hasVisibleSummary(summary),
    summaryMode: job.summary_mode || null,
  };
};

const createMemoryItem = (payload) => ({
  id: "current-summary",
  type: getSourceLabel(getSourceKeyByType(payload.type)),
  title: "Company Knowledge Base",
  description: payload.summary,
  rawType: payload.type,
  message: payload.message,
  recommendation: payload.recommendation,
  confidence: payload.confidence,
  grounded: payload.grounded,
  source: payload.source,
  reason: payload.reason,
  scrapeMetadata: payload.scrapeMetadata,
  hasUsableSummary: payload.hasUsableSummary,
  summaryMode: payload.summaryMode,
});

const scrollToSourceOptions = () => {
  document
    .getElementById("memory-options-section")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const MemoryScreen = ({ isSettings = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const hasLoadedOnceRef = useRef(false);
  const activeJobRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const hardTimeoutRef = useRef(null);

  const [memoryList, setMemoryList] = useState([]);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [manualContent, setManualContent] = useState("");
  const [manualFields, setManualFields] = useState(INITIAL_MANUAL_FIELDS);
  const [isFetchingSummary, setIsFetchingSummary] = useState(false);
  const [isMutatingSummary, setIsMutatingSummary] = useState(false);
  const [isSubmittingJob, setIsSubmittingJob] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [jobElapsedMs, setJobElapsedMs] = useState(0);
  const [generatedSummary, setGeneratedSummary] = useState("");
  const [previewEditorSeed, setPreviewEditorSeed] = useState("");
  const [generatedSummaryMeta, setGeneratedSummaryMeta] =
    useState(EMPTY_SUMMARY_META);
  const [jobFailure, setJobFailure] = useState(null);
  const [lastGenerateArgs, setLastGenerateArgs] = useState(null);

  const hasExistingSummary = memoryList.length > 0;
  const isProcessingActive = isSubmittingJob || isPolling;
  const processingSource =
    activeJob?.sourceType || selectedSource || lastGenerateArgs?.sourceKey;
  const processingJobId = activeJob?.jobId || null;

  // Firebase real-time progress
  const firebaseProgress = useSummaryProgress(isProcessingActive);
  const hasFirebaseData =
    firebaseProgress.isListening && firebaseProgress.state !== null;

  // Derive status/progress from Firebase when available, fall back to polling-based
  const processingStatus = hasFirebaseData
    ? firebaseProgress.state
    : activeJob?.status || (isSubmittingJob ? "preparing" : null);
  const progressValue = hasFirebaseData
    ? firebaseProgress.percentage
    : getStageProgress(
        activeJob?.status || (isSubmittingJob ? "preparing" : null),
        jobElapsedMs,
      );
  const firebaseMessage = hasFirebaseData ? firebaseProgress.message : "";
  const currentStageIndex = hasFirebaseData
    ? firebaseProgress.state === "extracting"
      ? 1
      : firebaseProgress.state === "generating"
        ? 3
        : firebaseProgress.state === "completed"
          ? PROCESSING_STAGES.length - 1
          : 0
    : getStageIndex(
        activeJob?.status || (isSubmittingJob ? "preparing" : null),
        jobElapsedMs,
      );

  const setTrackedJob = useCallback((nextJob) => {
    activeJobRef.current = nextJob;
    setActiveJob(nextJob);
  }, []);

  const clearPersistedJob = useCallback(() => {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(JOB_STORAGE_KEYS.id);
    window.localStorage.removeItem(JOB_STORAGE_KEYS.source);
    window.localStorage.removeItem(JOB_STORAGE_KEYS.startTime);
  }, []);

  const persistJob = useCallback((jobId, sourceKey, startTime) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(JOB_STORAGE_KEYS.id, jobId);
    window.localStorage.setItem(JOB_STORAGE_KEYS.source, sourceKey);
    window.localStorage.setItem(JOB_STORAGE_KEYS.startTime, String(startTime));
  }, []);

  const clearPollingTimers = useCallback(() => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (hardTimeoutRef.current) clearTimeout(hardTimeoutRef.current);
    pollTimeoutRef.current = null;
    timerIntervalRef.current = null;
    hardTimeoutRef.current = null;
  }, []);

  const resetSourceInputs = useCallback(() => {
    setWebsiteUrl("");
    setSelectedFile(null);
    setManualContent("");
    setManualFields(INITIAL_MANUAL_FIELDS);
    setSelectedSource(null);
  }, []);

  const applySummaryToList = useCallback((payload) => {
    if (payload.hasUsableSummary || payload.message) {
      setMemoryList([createMemoryItem(payload)]);
      return;
    }
    setMemoryList([]);
  }, []);

  const openPreview = useCallback((payload) => {
    setGeneratedSummary(payload.summary || "");
    setPreviewEditorSeed(payload.summary || "");
    setGeneratedSummaryMeta(payload);
    setActiveModal("preview");
  }, []);

  const fetchSummary = useCallback(async () => {
    // Only show full-screen loader on the very first load
    if (!hasLoadedOnceRef.current) {
      setIsFetchingSummary(true);
    }

    try {
      const response = await getRequest(
        navigate,
        authEndpoints.getCompanySummary,
      );

      if (response?.data?.status && response?.data?.data) {
        const normalizedPayload = normalizeSummaryPayload(response, null);

        if (
          !activeJobRef.current &&
          (normalizedPayload.hasUsableSummary || normalizedPayload.message)
        ) {
          setMemoryList([createMemoryItem(normalizedPayload)]);
        } else if (
          !activeJobRef.current &&
          !normalizedPayload.hasUsableSummary
        ) {
          setMemoryList([]);
        }
      } else if (!activeJobRef.current) {
        setMemoryList([]);
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    } finally {
      if (isMountedRef.current) {
        hasLoadedOnceRef.current = true;
        setIsFetchingSummary(false);
      }
    }
  }, [navigate]);

  const stopPolling = useCallback(
    ({ clearJob = false } = {}) => {
      clearPollingTimers();
      if (isMountedRef.current) {
        setIsPolling(false);
      }
      if (clearJob) {
        activeJobRef.current = null;
        if (isMountedRef.current) {
          setActiveJob(null);
        }
      }
    },
    [clearPollingTimers],
  );

  const handleJobFailedState = useCallback(
    (response, sourceKey, status = "failed") => {
      const failurePayload =
        response?.summary !== undefined
          ? response
          : normalizeSummaryPayload(response, getSourceTypeByKey(sourceKey));

      clearPersistedJob();
      stopPolling();

      if (!isMountedRef.current) return;

      setJobFailure({ ...failurePayload, status, sourceKey });
      setTrackedJob({
        ...(activeJobRef.current || {}),
        status,
        sourceType: sourceKey,
      });
      setActiveModal("processing");
    },
    [clearPersistedJob, stopPolling, setTrackedJob],
  );

  const handleJobCompletedState = useCallback(
    async (response, sourceKey) => {
      console.log(`Memory: Handling completed state for source ${sourceKey}`);
      const normalizedPayload = normalizeSummaryPayload(
        response,
        getSourceTypeByKey(sourceKey),
      );

      console.log("Memory: Normalized payload for preview:", normalizedPayload);

      clearPersistedJob();
      stopPolling();

      if (!isMountedRef.current) return;

      setJobFailure(null);
      applySummaryToList(normalizedPayload);
      openPreview(normalizedPayload);
      resetSourceInputs();
      dispatch(updateAvailableCredits(navigate));

      // KB is now confirmed created — wizard should show Continue while preview is open
      window.dispatchEvent(new CustomEvent("setup:kb-add-more-sources"));

      // We still fetch once to sync backend storage,
      // but fetchSummary is now guarded against overwriting
      console.log("Memory: Triggering final storage sync fetch...");
      fetchSummary();
    },
    [
      applySummaryToList,
      clearPersistedJob,
      dispatch,
      fetchSummary,
      navigate,
      openPreview,
      resetSourceInputs,
      stopPolling,
    ],
  );

  const scheduleNextPoll = useCallback((pollFn) => {
    pollTimeoutRef.current = setTimeout(pollFn, POLL_DELAY_MS);
  }, []);

  const pollJobStatus = useCallback(
    async ({ jobId, sourceKey, isRegenerate }) => {
      const currentJobId = String(jobId);

      console.log(
        `[Memory Poll] Executing poll hit for jobId: ${currentJobId}`,
      );

      if (!isMountedRef.current) {
        console.log(
          `[Memory Poll] Component unmounted, stopping poll for ${currentJobId}`,
        );
        return;
      }

      // Check against current tracked job ID to prevent race conditions
      const trackedJobId = activeJobRef.current?.jobId
        ? String(activeJobRef.current.jobId)
        : null;
      if (trackedJobId !== currentJobId) {
        console.log(
          `[Memory Poll] Job ID mismatch. Expected: ${trackedJobId}, got: ${currentJobId}. Aborting this poll chain.`,
        );
        return;
      }

      try {
        const url = `${authEndpoints.getSummaryJobStatus}?job_id=${currentJobId}`;
        console.log(`[Memory Poll] Fetching: ${url}`);

        const response = await getRequest(navigate, url);

        // Re-check after async fetch
        if (
          !isMountedRef.current ||
          String(activeJobRef.current?.jobId) !== currentJobId
        ) {
          console.log(
            `[Memory Poll] Stale response for ${currentJobId}, ignoring.`,
          );
          return;
        }

        const jobData = response?.data?.data || {};
        const jobInfo = jobData.job || {};
        const status = jobInfo.status || "queued";

        console.log(`[Memory Poll] Job ${currentJobId} status: ${status}`);

        // Update state with latest status
        setTrackedJob({
          ...activeJobRef.current,
          status,
        });

        if (status === "completed") {
          console.log(`[Memory Poll] Job ${currentJobId} completed!`);
          await handleJobCompletedState(response, sourceKey);
          return;
        }

        if (status === "failed" || status === "timeout") {
          console.warn(
            `[Memory Poll] Job ${currentJobId} reached terminal error state: ${status}`,
          );
          handleJobFailedState(response, sourceKey, status);
          return;
        }

        // Still in progress, schedule next poll in 5s
        console.log(
          `[Memory Poll] Job ${currentJobId} still ${status}. Scheduling next hit in ${POLL_DELAY_MS}ms...`,
        );
        scheduleNextPoll(() =>
          pollJobStatus({
            jobId: currentJobId,
            sourceKey,
            isRegenerate,
          }),
        );
      } catch (error) {
        console.error(
          `[Memory Poll] Request failed for ${currentJobId}:`,
          error,
        );

        if (
          !isMountedRef.current ||
          String(activeJobRef.current?.jobId) !== currentJobId
        )
          return;

        console.log(
          `[Memory Poll] Retrying in ${POLL_DELAY_MS}ms after error...`,
        );
        scheduleNextPoll(() =>
          pollJobStatus({
            jobId: currentJobId,
            sourceKey,
            isRegenerate,
          }),
        );
      }
    },
    [
      handleJobCompletedState,
      handleJobFailedState,
      navigate,
      scheduleNextPoll,
      setTrackedJob,
    ],
  );

  const startPolling = useCallback(
    ({ jobId, sourceKey, startTime, isRegenerate }) => {
      const stringJobId = String(jobId);
      console.log(`[Memory] Starting polling sequence for job: ${stringJobId}`);

      stopPolling({ clearJob: true });
      persistJob(stringJobId, sourceKey, startTime);
      setJobFailure(null);
      setIsPolling(true);

      const initialJob = {
        jobId: stringJobId,
        sourceType: sourceKey,
        status: "queued",
        startTime,
        isRegenerate,
      };

      setTrackedJob(initialJob);
      setJobElapsedMs(Date.now() - startTime);

      timerIntervalRef.current = setInterval(() => {
        if (!isMountedRef.current) return;
        const currentStartTime = activeJobRef.current?.startTime || startTime;
        setJobElapsedMs(Date.now() - currentStartTime);
      }, 1000);

      hardTimeoutRef.current = setTimeout(
        () => {
          if (
            !isMountedRef.current ||
            String(activeJobRef.current?.jobId) !== stringJobId
          )
            return;

          console.error(`[Memory] Hard timeout reached for job ${stringJobId}`);
          const timeoutPayload = {
            ...EMPTY_SUMMARY_META,
            recommendation:
              sourceKey === "website"
                ? "Try uploading a PDF or entering manual details for a more reliable result."
                : "Try simplifying the source input and running the generation again.",
            reason: "The AI job exceeded the expected processing window.",
            source: getSourceLabel(sourceKey),
          };

          handleJobFailedState(timeoutPayload, sourceKey, "timeout");
        },
        Math.max(0, JOB_TIMEOUT_MS - (Date.now() - startTime)),
      );

      // Trigger first hit immediately
      pollJobStatus({ jobId: stringJobId, sourceKey, isRegenerate });
    },
    [
      handleJobFailedState,
      persistJob,
      pollJobStatus,
      setTrackedJob,
      stopPolling,
    ],
  );

  const toBase64 = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      }),
    [],
  );

  const buildPayload = useCallback(
    async (sourceKey) => {
      const payload = { type: getSourceTypeByKey(sourceKey) };

      if (sourceKey === "website") {
        const trimmedUrl = websiteUrl.trim();
        if (!trimmedUrl) {
          EventEmitter.emit(EMITTER_ERROR, "Enter a website URL to continue.");
          return null;
        }

        payload.url = /^https?:\/\//i.test(trimmedUrl)
          ? trimmedUrl
          : `https://${trimmedUrl}`;
      }

      if (sourceKey === "pdf") {
        if (!selectedFile) {
          EventEmitter.emit(EMITTER_ERROR, "Upload a PDF to continue.");
          return null;
        }

        payload.pdf = await toBase64(selectedFile);
      }

      if (sourceKey === "manual") {
        let combinedText = "";

        if (manualFields.website)
          combinedText += `Website: ${manualFields.website}\n`;
        if (manualFields.companyName)
          combinedText += `Company Name: ${manualFields.companyName}\n`;
        if (manualFields.businessDescription) {
          combinedText += `Business Description: ${manualFields.businessDescription}\n`;
        }
        if (manualFields.awards)
          combinedText += `Awards: ${manualFields.awards}\n`;
        if (combinedText && manualContent) combinedText += "\n";
        combinedText += manualContent.replace(/<[^>]*>/g, " ").trim();

        if (!combinedText.trim()) {
          EventEmitter.emit(
            EMITTER_ERROR,
            "Add company details before generating manual knowledge.",
          );
          return null;
        }

        payload.text = combinedText;
      }

      return payload;
    },
    [manualContent, manualFields, selectedFile, toBase64, websiteUrl],
  );

  const handleGenerate = useCallback(
    async (sourceKey) => {
      if (!SOURCE_CONFIG[sourceKey]) return;

      const isRegenerate = hasExistingSummary;
      console.log(
        `Memory: Starting ${isRegenerate ? "regeneration" : "generation"} from ${sourceKey}`,
      );

      setTrackedJob(null);
      setLastGenerateArgs({ sourceKey, isRegenerate });
      setIsSubmittingJob(true);
      setJobFailure(null);
      setJobElapsedMs(0);
      setActiveModal("processing");

      try {
        const payload = await buildPayload(sourceKey);
        if (!payload) {
          console.warn("Memory: Payload generation failed or aborted.");
          setActiveModal("source");
          return;
        }

        const startTime = Date.now();
        const endpoint = isRegenerate
          ? authEndpoints.regenerateSummary
          : authEndpoints.generateSummary;

        console.log(`Memory: Sending request to ${endpoint}`);
        const response = await postRequest(navigate, endpoint, payload);

        if (!isMountedRef.current) return;

        if (response?.status === 202 || response?.data?.status) {
          const jobId = response?.data?.data?.job_id;

          if (jobId) {
            console.log(
              `Memory: Job created with ID ${jobId}. Starting polling...`,
            );
            startPolling({ jobId, sourceKey, startTime, isRegenerate });
          } else {
            console.log("Memory: Job completed synchronously.");
            await handleJobCompletedState(response, sourceKey);
          }
        } else {
          console.error(
            "Memory: Request failed with status:",
            response?.status,
          );
          setActiveModal("source");
          EventEmitter.emit(
            EMITTER_ERROR,
            response?.data?.message || "Failed to initiate generation.",
          );
        }
      } catch (error) {
        console.error("Error generating summary:", error);
        if (isMountedRef.current) {
          setActiveModal("source");
        }
        EventEmitter.emit(
          EMITTER_ERROR,
          error?.response?.data?.message ||
            "Failed to generate company summary.",
        );
      } finally {
        if (isMountedRef.current) {
          setIsSubmittingJob(false);
        }
      }
    },
    [
      buildPayload,
      handleJobCompletedState,
      hasExistingSummary,
      navigate,
      setTrackedJob,
      startPolling,
    ],
  );

  const handleRetry = useCallback(() => {
    if (!lastGenerateArgs?.sourceKey) return;

    if (
      jobFailure?.sourceKey &&
      jobFailure.sourceKey !== lastGenerateArgs.sourceKey
    ) {
      setSelectedSource(jobFailure.sourceKey);
    }

    handleGenerate(lastGenerateArgs.sourceKey);
  }, [handleGenerate, jobFailure?.sourceKey, lastGenerateArgs]);

  const handleEditSummary = useCallback(async () => {
    if (!hasVisibleSummary(generatedSummary)) {
      EventEmitter.emit(
        EMITTER_ERROR,
        "No grounded summary is available to save yet. Use a PDF upload or manual text.",
      );
      return;
    }

    setIsMutatingSummary(true);

    try {
      const response = await patchRequest(
        navigate,
        authEndpoints.editCompanySummary,
        { summary: generatedSummary },
      );

      if (response?.data?.status) {
        setActiveModal(null);
        fetchSummary();
        window.dispatchEvent(new CustomEvent("setup:kb-auto-advance"));
      }
    } catch (error) {
      console.error("Error editing summary:", error);
    } finally {
      if (isMountedRef.current) {
        setIsMutatingSummary(false);
      }
    }
  }, [fetchSummary, generatedSummary, navigate]);

  const handleDeleteSummary = useCallback(async () => {
    setIsMutatingSummary(true);

    try {
      const response = await deleteRequest(
        authEndpoints.deleteCompanySummary,
        {},
        navigate,
      );

      if (response?.data?.status) {
        setMemoryList([]);
        setActiveModal(null);
        window.dispatchEvent(new CustomEvent("setup:kb-deleted"));
      }
    } catch (error) {
      console.error("Error deleting summary:", error);
    } finally {
      if (isMountedRef.current) {
        setIsMutatingSummary(false);
      }
    }
  }, [navigate]);

  const handleOpenPreviewFromItem = useCallback(
    (item) => {
      const payload = {
        ...EMPTY_SUMMARY_META,
        type: item.rawType,
        summary: item.description,
        message: item.message,
        recommendation: item.recommendation,
        confidence: item.confidence,
        grounded: item.grounded,
        reason: item.reason,
        source: item.source,
        scrapeMetadata: item.scrapeMetadata,
        hasUsableSummary: item.hasUsableSummary,
        summaryMode: item.summaryMode,
      };

      openPreview(payload);
    },
    [openPreview],
  );

  const handleSourceCardClick = useCallback(
    (sourceKey) => {
      if (isProcessingActive) {
        setActiveModal("processing");
        return;
      }

      setSelectedSource(sourceKey);
      setJobFailure(null);
      setActiveModal("source");
    },
    [isProcessingActive],
  );

  const handleRestorePersistedJob = useCallback(() => {
    if (typeof window === "undefined") return;

    const jobId = window.localStorage.getItem(JOB_STORAGE_KEYS.id);
    const sourceKey = window.localStorage.getItem(JOB_STORAGE_KEYS.source);
    const startTime = Number(
      window.localStorage.getItem(JOB_STORAGE_KEYS.startTime),
    );

    if (!jobId || !SOURCE_CONFIG[sourceKey] || !startTime) {
      clearPersistedJob();
      return;
    }

    setSelectedSource(sourceKey);
    setActiveModal("processing");
    startPolling({
      jobId,
      sourceKey,
      startTime,
      isRegenerate: true,
    });
  }, [clearPersistedJob, startPolling]);

  useEffect(() => {
    fetchSummary();
    handleRestorePersistedJob();

    return () => {
      isMountedRef.current = false;
      stopPolling({ clearJob: true });
    };
  }, [fetchSummary, handleRestorePersistedJob, stopPolling]);

  useEffect(() => {
    if (!activeJobRef.current?.startTime || !isProcessingActive) return;
    setJobElapsedMs(Date.now() - activeJobRef.current.startTime);
  }, [isProcessingActive]);

  // React to Firebase completion/failure in real-time
  const firebaseHandledRef = useRef(null);
  useEffect(() => {
    if (!hasFirebaseData || !isProcessingActive) return;
    const jobId = activeJobRef.current?.jobId;
    if (!jobId) return;

    // Prevent handling the same jobId + state combo twice
    const stateKey = `${jobId}_${firebaseProgress.state}`;
    if (firebaseHandledRef.current === stateKey) return;

    if (firebaseProgress.isCompleted) {
      firebaseHandledRef.current = stateKey;
      // Firebase says done - fetch the final summary from backend
      const sourceKey =
        activeJobRef.current?.sourceType || lastGenerateArgs?.sourceKey;
      clearPersistedJob();
      stopPolling();
      // Fetch the completed summary and open preview
      (async () => {
        try {
          const response = await getRequest(
            navigate,
            authEndpoints.getCompanySummary,
          );
          if (isMountedRef.current) {
            await handleJobCompletedState(response, sourceKey);
          }
        } catch (err) {
          console.error("[Firebase] Failed to fetch completed summary:", err);
        }
      })();
    } else if (firebaseProgress.isFailed) {
      firebaseHandledRef.current = stateKey;
      const sourceKey =
        activeJobRef.current?.sourceType || lastGenerateArgs?.sourceKey;
      const failPayload = {
        ...EMPTY_SUMMARY_META,
        reason: firebaseProgress.message || "The AI service encountered an error.",
        source: getSourceLabel(sourceKey),
      };
      handleJobFailedState(failPayload, sourceKey, "failed");
    }
  }, [
    hasFirebaseData,
    firebaseProgress.isCompleted,
    firebaseProgress.isFailed,
    firebaseProgress.state,
    firebaseProgress.message,
    isProcessingActive,
    clearPersistedJob,
    stopPolling,
    navigate,
    handleJobCompletedState,
    handleJobFailedState,
    lastGenerateArgs,
  ]);

  return (
    <div
      className={`${classes.wrapper} ${isSettings ? classes.settingsWrapper : ""}`}
    >
      {!isSettings && (
        <CustomHeader items={breadcrumbs} description={STRINGS.MEMORY_DESC} />
      )}

      <div
        className={`${classes.mainContainer} ${isSettings ? classes.settingsMainContainer : ""}`}
      >
        <div id="memory-options-section" className={classes.optionsRow}>
          <MemoryOption
            title={
              hasExistingSummary
                ? STRINGS.SCRAPING_TITLE_ADD
                : STRINGS.SCRAPING_TITLE
            }
            description={
              hasExistingSummary
                ? STRINGS.SCRAPING_DESC_ADD
                : STRINGS.SCRAPING_DESC
            }
            asset={LANDING_PAGE}
            onClick={() => handleSourceCardClick("website")}
          />
          <MemoryOption
            title={
              hasExistingSummary ? STRINGS.PDF_TITLE_ADD : STRINGS.PDF_TITLE
            }
            description={
              hasExistingSummary ? STRINGS.PDF_DESC_ADD : STRINGS.PDF_DESC
            }
            asset={PDF_IMAGE}
            onClick={() => handleSourceCardClick("pdf")}
          />
          <MemoryOption
            title={
              hasExistingSummary
                ? STRINGS.MANUAL_TITLE_ADD
                : STRINGS.MANUAL_TITLE
            }
            description={
              hasExistingSummary ? STRINGS.MANUAL_DESC_ADD : STRINGS.MANUAL_DESC
            }
            asset={NOTE_ICON}
            onClick={() => handleSourceCardClick("manual")}
          />
        </div>

        <div className={classes.summarySection}>
          {memoryList.length > 0 && (
            <div className={classes.sectionHeader}>
              <TypographyInline
                label="Company Knowledge Base"
                size="xl"
                fontWeight={fontWeight.BOLD}
                color={BASE.BLACK}
              />
            </div>
          )}

          <div
            className={classes.summaryList}
            style={{ position: "relative", minHeight: "200px" }}
          >
            {isFetchingSummary && memoryList.length === 0 ? (
              <DashboardLoader message="Loading your knowledge base..." />
            ) : memoryList.length > 0 ? (
              memoryList.map((item) => (
                <SummaryItem
                  key={item.id}
                  type={item.type}
                  title={item.title}
                  description={item.description}
                  message={item.message}
                  recommendation={item.recommendation}
                  confidence={item.confidence}
                  grounded={item.grounded}
                  source={item.source}
                  onEdit={() => handleOpenPreviewFromItem(item)}
                  onDelete={() => setActiveModal("delete")}
                />
              ))
            ) : (
              <MemoryEmptyState data={STRINGS.EMPTY_DATA} />
            )}
          </div>
        </div>
      </div>

      <MemorySourceModal
        open={activeModal === "source"}
        selectedSource={selectedSource}
        sourceOptions={Object.values(SOURCE_CONFIG)}
        hasExistingSummary={hasExistingSummary}
        websiteUrl={websiteUrl}
        onWebsiteChange={setWebsiteUrl}
        selectedFile={selectedFile}
        onFileChange={setSelectedFile}
        manualFields={manualFields}
        onManualFieldChange={setManualFields}
        manualContent={manualContent}
        onManualContentChange={(value) =>
          setManualContent(extractEditorHtml(value))
        }
        onClose={() => setActiveModal(null)}
        onSelectSource={setSelectedSource}
        onGenerate={handleGenerate}
        isGenerating={isSubmittingJob}
      />

      <MemoryProcessingModal
        open={activeModal === "processing"}
        status={processingStatus}
        sourceLabel={getSourceLabel(processingSource)}
        jobId={processingJobId}
        elapsedLabel={formatElapsedTime(jobElapsedMs)}
        stages={PROCESSING_STAGES}
        currentStageIndex={currentStageIndex}
        progress={progressValue}
        tips={PROCESSING_TIPS}
        errorState={jobFailure}
        firebaseMessage={firebaseMessage}
        onClose={() => setActiveModal(null)}
        onRetry={lastGenerateArgs?.sourceKey ? handleRetry : null}
        onSwitchSource={(sourceKey) => {
          setSelectedSource(sourceKey);
          setJobFailure(null);
          setActiveModal("source");
        }}
      />

      <MemoryPreviewModal
        open={activeModal === "preview"}
        editorSeed={previewEditorSeed}
        metadata={generatedSummaryMeta}
        sourceLabel={getSourceLabel(
          getSourceKeyByType(generatedSummaryMeta.type),
        )}
        onSummaryChange={(value) =>
          setGeneratedSummary(extractEditorHtml(value))
        }
        onClose={() => {
          setActiveModal(null);
          setGeneratedSummaryMeta(EMPTY_SUMMARY_META);
          setPreviewEditorSeed("");
          window.dispatchEvent(new CustomEvent("setup:kb-auto-advance"));
        }}
        onAddMoreSources={() => {
          setActiveModal(null);
          setSelectedSource(null);
          scrollToSourceOptions();
          window.dispatchEvent(new CustomEvent("setup:kb-add-more-sources"));
        }}
        onSave={handleEditSummary}
        isSaving={isMutatingSummary}
      />

      <MemoryDeleteModal
        open={activeModal === "delete"}
        onClose={() => setActiveModal(null)}
        onConfirm={handleDeleteSummary}
        isDeleting={isMutatingSummary}
      />
    </div>
  );
};

export default MemoryScreen;
